import type { FinanceReadinessPreviewTier } from "../../lib/dashboard";

export type NextMoveWidgetKind =
  | "offers-expiry"
  | "data-source"
  | "profile-completeness";

export interface SelectNextMoveWidgetInput {
  /** Gate `off` — never choose `publish` or `offers-expiry`; no marketplace
   *  copy, no marketplace read, in this branch (tech-spec gate matrix). */
  readinessOnly: boolean;
  /** `snapshot.portalDiscoverable` — ignored when `readinessOnly`. */
  isLive: boolean;
  verificationTier: FinanceReadinessPreviewTier;
  /** The top made-for-you offer's `expires_at`, when live and one exists. */
  topOfferExpiresAt?: string | null;
  now?: Date;
}

const EXPIRY_WINDOW_DAYS = 4;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Plan 435 M3 (mfh-next-move). Chooses exactly ONE `JourneyRail` widget by
 * state (roadmap/design §4 row 4): a top made-for-you offer expiring within
 * four days outranks everything else (it is the most time-sensitive);
 * then a self-reported (T1) buyer → `DataSourceChooser`; everything else (including a
 * fully-verified, fully-complete buyer) falls through to
 * `ProfileCompletenessWidget`, which renders its own "nothing left to add"
 * state gracefully rather than this function needing a fifth "none" case.
 * `readinessOnly` (gate `off`) skips the first two branches entirely —
 * never a marketplace promise outside the gate.
 */
export function selectNextMoveWidget(
  input: SelectNextMoveWidgetInput,
): NextMoveWidgetKind {
  const { readinessOnly, isLive, verificationTier, topOfferExpiresAt } = input;
  const now = input.now ?? new Date();

  if (!readinessOnly && isLive && topOfferExpiresAt) {
    const expires = new Date(topOfferExpiresAt);
    if (!Number.isNaN(expires.getTime())) {
      const daysLeft = (expires.getTime() - now.getTime()) / MS_PER_DAY;
      if (daysLeft >= 0 && daysLeft <= EXPIRY_WINDOW_DAYS) {
        return "offers-expiry";
      }
    }
  }

  // Unpublished no longer picks "publish": the hero's primary CTA is
  // already "Go live to agents", so a second publish ask below it was a
  // duplicate. The slot falls through to the next readiness step instead.

  if (verificationTier === "self-reported") return "data-source";

  return "profile-completeness";
}
